import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaPhone, FaEnvelope, FaMapMarkerAlt, FaShoppingBag, FaChevronDown } from 'react-icons/fa';
import Logo from '../assets/Logo.svg';

function Header() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsProductsDropdownOpen(false);
    }, [location]);

    const categories = [
        { name: 'All Products', path: '/products' },
        { name: 'Men\'s Wear', path: '/products?category=men' },
        { name: 'Women\'s Wear', path: '/products?category=women' },
        { name: 'Kids Wear', path: '/products?category=kids' }
    ];

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Products', path: '/products', hasDropdown: true },
        { name: 'Contact', path: '/contact' }
    ];

    return (
        <>
            {/* Top Bar */}
            <div className="bg-primary-700 text-white py-2 px-4 text-sm hidden md:block">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-6">
                        <a href="tel:+919585284627" className="flex items-center gap-2 hover:text-yellow-300 transition">
                            <FaPhone className="text-xs" />
                            <span>+91 95852 84627</span>
                        </a>
                        <a href="mailto:sstexonline@gmail.com" className="flex items-center gap-2 hover:text-yellow-300 transition">
                            <FaEnvelope className="text-xs" />
                            <span>sstexonline@gmail.com</span>
                        </a>
                    </div>
                    <div className="flex items-center gap-2 text-white/90">
                        <FaMapMarkerAlt className="text-xs" />
                        <span>Tiruppur, Tamil Nadu</span>
                    </div>
                </div>
            </div>

            {/* Main Header */}
            <header className={`sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? 'shadow-lg' : 'shadow-md'}`}>
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3 group">
                            <img src={Logo} alt="SS Tex Logo" className="h-12 w-12 transform group-hover:scale-110 transition duration-300" />
                            <div>
                                <div className="text-2xl font-bold text-primary-600 group-hover:text-primary-700 transition">SS Tex</div>
                                <div className="text-xs text-gray-600 -mt-1">Premium Garments</div>
                            </div>
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center gap-8">
                            {navLinks.map((link) => (
                                link.hasDropdown ? (
                                    <div
                                        key={link.name}
                                        className="relative"
                                        onMouseEnter={() => setIsProductsDropdownOpen(true)}
                                        onMouseLeave={() => setIsProductsDropdownOpen(false)}
                                    >
                                        <NavLink
                                            to={link.path}
                                            className={({ isActive }) =>
                                                `flex items-center gap-1 font-semibold transition ${isActive ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
                                                }`
                                            }
                                        >
                                            {link.name}
                                            <FaChevronDown className={`text-xs transition-transform ${isProductsDropdownOpen ? 'rotate-180' : ''}`} />
                                        </NavLink>
                                        {isProductsDropdownOpen && (
                                            <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 animate-fade-in">
                                                {categories.map((cat) => (
                                                    <Link
                                                        key={cat.path}
                                                        to={cat.path}
                                                        className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition font-medium"
                                                    >
                                                        {cat.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <NavLink
                                        key={link.name}
                                        to={link.path}
                                        className={({ isActive }) =>
                                            `font-semibold transition ${isActive ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
                                            }`
                                        }
                                    >
                                        {link.name}
                                    </NavLink>
                                )
                            ))}
                        </nav>

                        {/* CTA Button */}
                        <Link
                            to="/contact"
                            className="hidden lg:flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 transition transform hover:scale-105 shadow-md"
                        >
                            <FaShoppingBag />
                            Get Quote
                        </Link>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="lg:hidden text-gray-700 hover:text-primary-600 transition p-2"
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? <FaTimes className="text-2xl" /> : <FaBars className="text-2xl" />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="lg:hidden bg-white border-t border-gray-200 shadow-xl animate-fade-in">
                        <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
                            {navLinks.map((link) => (
                                link.hasDropdown ? (
                                    <div key={link.name}>
                                        <NavLink
                                            to={link.path}
                                            className={({ isActive }) =>
                                                `block px-4 py-3 rounded-lg font-semibold transition ${isActive ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-100'
                                                }`
                                            }
                                        >
                                            {link.name}
                                        </NavLink>
                                        <div className="pl-4 mt-1 space-y-1">
                                            {categories.slice(1).map((cat) => (
                                                <Link
                                                    key={cat.path}
                                                    to={cat.path}
                                                    className="block px-4 py-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition"
                                                >
                                                    {cat.name}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <NavLink
                                        key={link.name}
                                        to={link.path}
                                        className={({ isActive }) =>
                                            `block px-4 py-3 rounded-lg font-semibold transition ${isActive ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-100'
                                            }`
                                        }
                                    >
                                        {link.name}
                                    </NavLink>
                                )
                            ))}
                            <Link
                                to="/contact"
                                className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 transition mt-4"
                            >
                                <FaShoppingBag />
                                Get Quote
                            </Link>
                            <div className="pt-4 border-t border-gray-200 mt-4 space-y-2">
                                <a href="tel:+919585284627" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-primary-600 transition">
                                    <FaPhone className="text-sm" />
                                    <span className="text-sm">+91 95852 84627</span>
                                </a>
                                <a href="mailto:sstexonline@gmail.com" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-primary-600 transition">
                                    <FaEnvelope className="text-sm" />
                                    <span className="text-sm">sstexonline@gmail.com</span>
                                </a>
                            </div>
                        </nav>
                    </div>
                )}
            </header>
        </>
    );
}

export default Header;